import React, { useState, Fragment, useEffect, useMemo } from 'react';

import { getTutorials } from '../../../store/tutorials';
import { getCoverData, initialFVICoverData } from '../../../store/coverData';
import { getReportDetails, setItemsPerPage, setLastPhotoNumber } from '../../../store/reportDetails';
import { useAppDispatch, useAppSelector } from '../../../store/configureStore';

import { DEFAULT_REPORT_TITLE, TABLE_ITEM_COUNT_LIMITS, tutorialConstants } from '../../../utils/constants';
import { CustomUTMOverview, NewAnomalyItem, PDFImageProps, PIDData, UTMInspection, User } from '../../../utils/models';
import { getCurrentUser } from '../../../services/authService';
import { getStructureAnomalyClassOptions } from '../../../utils/data';
import { matchPhrases, reArrangeAnomalies, reArrangePhotoRecord, validateWorkOrderNumber } from '../../../utils/utilities';

import Button from '../../themed/button';
import Conditional from '../../common/conditional';
import CustomCoverForm from '../../forms/custom-fvi/coverForm';
import CustomUTMStructureSummaryOverviewForm from '../../forms/custom-utm/customUtmStructureSummaryOverviewForm';
import CustomExxonMobilUTMStructureReport from './customExxonMobilUTMStructureReport';
import CustomAnomalyItems from '../custom-general/custom-anomaly-items';
import CustomUTMSection from './custom-utm-section';
import Elevation from '../../themed/instruction-modals/elevation';
import LoadingBadge from '../../themed/loading-badge';
import Modal from '../../themed/modal';
import FVICoverData from '../../themed/fvi-cover-data';
import FVIStructurePIDImageForm from '../../forms/custom-fvi/fviStructurePIDImageForm';
import MUTPhotograpicRecord from '../../forms/mut/mutPhotographicRecord';
import ReportTitleForm from '../../forms/custom-fvi/reportTitleForm';
import SyncTime from '../../blocks/sync-time';
import VIGeneralDrawingForm from '../../forms/visual-inspection/viGeneralDrawingForm';
import Zippy from '../../themed/zippy';

import useAutoSaveReport from '../../../hooks/useAutoSave';
import useInspectedItemIDUpdate from '../../../hooks/useInspectedItemIDUpdate';
import useProjectDetails from '../../../hooks/useProjectDetails';
import useAnomalyRecords from '../../../hooks/useAnomalyRecords';

import config from '../../../utils/config';
import toastService from '../../../services/toastService';

const CreateCustomExxonMobilUTMStructureReport: React.FC = () => {
	const { fviCoverData } = useAppSelector(getCoverData);
	const { isSaving } = useAppSelector(getReportDetails);

	const [reportTitle, setReportTitle] = useState(DEFAULT_REPORT_TITLE);
	const [coverData, setCoverData] = useState(initialFVICoverData);
	const [summary, setSummary] = useState({ hasAnomalies: '', itemName: '', overview: '' });
	const [anomalyItems, setAnomalyItems] = useState<NewAnomalyItem[]>([]);
	const [pidData, setPIDData] = useState<PIDData>({ hasPID: 'yes', pIDNumber: '', pIDImages: [] });
	const [generalDrawing, setGeneralDrawing] = useState<PDFImageProps[]>([]);
	const [photographicData, setPhotographicData] = useState<PDFImageProps[]>([]);
	const [previewReport, setPreviewReport] = useState<boolean>(false);
	const [isoImage, setIsoImage] = useState<PDFImageProps[]>([]);
	const [utInspectionOverview, setUTInspectionOverview] = useState(initialUTMOverview);
	const [utInspectionItems, setUTInspectionItems] = useState<UTMInspection[]>([]);

	const { projectDetails, error } = useProjectDetails();
	const { activeTutorial } = useAppSelector(getTutorials);
	const { role } = getCurrentUser() as User;

	const reportDetails = useMemo(() => {
		return {
			reportTitle,
			coverData,
			summary,
			anomalyItems,
			pidData,
			generalDrawing,
			photographicData,
			projectDetails,
			isoImage,
			inspectionOverview: utInspectionOverview,
			utInspectionItems,
		};
	}, [anomalyItems, projectDetails, coverData, isoImage, pidData, generalDrawing, summary, photographicData, reportTitle, utInspectionOverview, utInspectionItems]);

	const inspectionItemReadingsMissing = useMemo(() => {
		if (utInspectionItems.length === 0) return true;

		return utInspectionItems.some((item) => !item.sectionTitle || item.items.length === 0);
	}, [utInspectionItems]);

	const validateReportCompletion = () => {
		if (!validateWorkOrderNumber(coverData.workOrderNumber))
			return 'Enter a valid work order number!';

		if (summary.itemName.length === 0) {
			return 'Please complete summary information!';
		}

		if (matchPhrases(pidData.hasPID, 'yes') && pidData.pIDImages.length === 0) {
			return 'P&ID images missing!';
		}

		if (matchPhrases(summary.hasAnomalies, 'yes')) {
			if (anomalyItems.length === 0) {
				return 'Please upload at least one anomaly!.'
			}
		}
		
		if (photographicData.length < 2) {
			return 'Please provide at least 2 photos!';
		}

		if (isoImage.length === 0) {
			return 'Please upload Isometric drawing!';
		}

		if (utInspectionOverview.equipment.length === 0) {
			return 'Please complete the inspection overview!';
		}
		
		if (inspectionItemReadingsMissing) {
			return 'Please complete the inspection items section!';
		}

		return '';
	}

	const togglePreviewReport = (): void => {
		if (config.enableReportCompletionBeforePublish) {
			const validationMessage = validateReportCompletion();
			if (validationMessage) return toastService.error(validationMessage);
		}
		
		setPreviewReport((p: boolean) => !p);
	};

	const handleSaveAndPreview = () => {
		autoSave.saveReport();
		togglePreviewReport();
	};

	const autoSave = useAutoSaveReport(reportDetails);
	const dispatch = useAppDispatch();

	useAnomalyRecords(anomalyItems);
	useInspectedItemIDUpdate([summary.itemName]);

	useEffect(() => {
		function initializeReport() {
			const cachedReport = autoSave.oldReportData as typeof reportDetails | null;
			if (!cachedReport) {
				if (fviCoverData.coverData.length > 0) setCoverData(fviCoverData);

				return;
			}

			
			setReportTitle(cachedReport.reportTitle);
			setCoverData(cachedReport.coverData?.coverData?.length > 0 ? cachedReport.coverData : fviCoverData);
			setAnomalyItems(reArrangeAnomalies(cachedReport.anomalyItems));
			setSummary(cachedReport.summary);
			setPIDData(cachedReport.pidData);
			setGeneralDrawing(cachedReport.generalDrawing ?? []);
			setUTInspectionOverview(cachedReport.inspectionOverview);
			setUTInspectionItems(cachedReport.utInspectionItems);
			setIsoImage(cachedReport.isoImage);

			const photoRecord = reArrangePhotoRecord(cachedReport.photographicData);
			dispatch(setLastPhotoNumber(photoRecord.length));
			setPhotographicData(photoRecord);
		}

		initializeReport();
	}, [autoSave.oldReportData, dispatch, fviCoverData]);

	useEffect(() => {
		dispatch(setItemsPerPage(TABLE_ITEM_COUNT_LIMITS.UTM));
	}, [dispatch]);

	return (
		<Fragment>
			<Modal isVisible={previewReport} onClose={togglePreviewReport}>
				<CustomExxonMobilUTMStructureReport {...reportDetails} />
			</Modal>

			<div className="mb-4">
				<SyncTime lastSynced={autoSave.lastSaved} />
			</div>

			{projectDetails && (
				<FVICoverData
					coverDataId={config.coverDataTemplates.exxonMobilUTMStructure[projectDetails.organisationId]}
					isVisible={projectDetails?.inspectionTypeId === config.supportedTemplates.exxonMobilUTMStructure && role.approveReports}
					subtitle={`Update the Cover Data information. This is the default data used on all ${projectDetails.organisationName} UTM Structural Reports`}
				/>
			)}

			<Zippy headerTitle="Report Title" isValid={reportTitle.length > 0}>
				<ReportTitleForm projectTitle={projectDetails?.projectTitle} onAdd={setReportTitle} />
			</Zippy>

			<Zippy headerTitle="Cover Sheet" isValid={coverData.coverData.length > 0}>
				<CustomCoverForm initialData={coverData} onAddCoverForm={setCoverData} />
			</Zippy>
			
			<Zippy headerTitle="Executive Summary" isValid={summary.itemName.length > 0}>
				<CustomUTMStructureSummaryOverviewForm initialSummary={summary} onSave={setSummary} />
			</Zippy>

			<Conditional isVisible={matchPhrases(summary.hasAnomalies, 'yes')}>
				<Zippy headerTitle="Anomaly Items" isValid={anomalyItems.length > 0}>
					<CustomAnomalyItems
						isPDOFormat={false}
						nextAnomalyID={anomalyItems.length + 1}
						locationOptions={[]}
						isNewFormat={false}
						damageClasses={getStructureAnomalyClassOptions()}
						items={anomalyItems}
						onDataChange={(items) => setAnomalyItems(items)}
					/>
				</Zippy>
			</Conditional>

			<Zippy headerTitle="Photographic Report" isValid={photographicData.length > 0}>
				<MUTPhotograpicRecord initialImages={photographicData} onProceed={setPhotographicData} />
			</Zippy>

			<Zippy headerTitle="P & ID" isValid={matchPhrases(pidData.hasPID, 'yes') ? pidData.pIDImages.length > 0 : true}>
				<FVIStructurePIDImageForm initialPIDData={pidData} onAdd={setPIDData} />
			</Zippy>

			<Zippy headerTitle="General Drawing (Optional)" isValid={true}>
				<VIGeneralDrawingForm initialGeneralDrawing={generalDrawing} onProceed={setGeneralDrawing} />
			</Zippy>

			<CustomUTMSection
				tmlLocations={isoImage}
				onChangeTMLLocations={setIsoImage}
				utmItems={utInspectionItems}
				utmOverview={utInspectionOverview}
				onItemsChange={setUTInspectionItems}
				onOverviewChange={setUTInspectionOverview}
			/>

			<div className="d-flex justify-content-between align-items-center">
				<div>
					<Conditional isVisible={autoSave.isProcessing}>
						<LoadingBadge message="Saving" />
					</Conditional>
				</div>

				<Elevation isElevated={activeTutorial === tutorialConstants.PREVIEW_REPORT}>
					<Button
						label="Save and Preview Report"
						disabled={error || autoSave.isProcessing || isSaving}
						onClick={handleSaveAndPreview}
					/>
				</Elevation>
			</div>
		</Fragment>
	);
};

const initialUTMOverview: Omit<CustomUTMOverview, 'data'> = {
	ndeTechnique: '',
	equipment: '',
	probe: '',
	amplitudeLinearityCheck: '',
	screenHeightLinearityCheck: '',
	calibrationBlk: '',
	refStd: '',
	serialNo: '',
	scanningLevel: '',
	couplant: '',
	remarks: '',
	material: '',
	calibrationDueDate: new Date(),
	dateOfUTM: null,
	corrosionAllowance: 0,
	unit: 'mm',
	summary: '',
	techniqueType: '',
	velocity: 0,
};

export default CreateCustomExxonMobilUTMStructureReport;
